'use client';

import { type ReactNode, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import {
  ChevronRight,
  Ban,
  Download,
  Link2,
  Check,
  DraftingCompass,
  Calculator,
} from 'lucide-react';
import Image from 'next/image';
import { PulsingDots } from '@/shared/ui';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/ui/shadcn/drawer';
import { Button } from '@/shared/ui/shadcn/button';
import { Progress } from '@/shared/ui/shadcn/progress';
import { useQueueLimit } from '../hooks/use-queue-limit';
import { rendersService } from '@/shared/api/services/renders.service';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { calculateJobProgressFromStart } from '@/shared/lib/progress';
import type { JobHistoryItem } from '@/features/design/history';
import {
  isRenderHistoryItem,
  isAlbumHistoryItem,
  isEstimateHistoryItem,
  useActiveJobs,
} from '@/features/design/history';
import { cn } from '@/shared/lib/cn';

interface JobQueueSheetProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Badge component like iOS
function Badge({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: 'main' | 'secondary' | '4k' | 'album';
}) {
  const colors = {
    main: 'bg-green-500',
    secondary: 'bg-primary',
    '4k': 'bg-primary',
    album: 'bg-blue-500',
  };

  return (
    <span
      className={cn(
        'text-[9px] font-semibold text-white px-1.5 py-0.5 rounded-full uppercase',
        colors[variant]
      )}
    >
      {children}
    </span>
  );
}

export function JobQueueSheet({
  children,
  open: controlledOpen,
  onOpenChange,
}: JobQueueSheetProps) {
  const t = useTranslations('render.queue');
  const tConcept = useTranslations('concept');
  const tConceptCard = useTranslations('conceptCard');
  const tErrors = useTranslations('common.errors');
  const { isQueueFull, hasQueueLimit } = useQueueLimit();
  const [cancellingRenders, setCancellingRenders] = useState<Set<string>>(
    new Set()
  );
  const [internalOpen, setInternalOpen] = useState(false);

  // Get all jobs from hook
  const { allJobs } = useActiveJobs();

  // Use controlled or internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalOpen(open);
    }
  };

  // Use only first 5 jobs
  const jobsToShow = allJobs.slice(0, 5);

  // Check if job can be cancelled (only pending renders)
  const canCancelJob = (job: JobHistoryItem): boolean => {
    return isRenderHistoryItem(job) && job.attributes.status === 'pending';
  };

  const handleCancelJob = async (jobId: string) => {
    if (cancellingRenders.has(jobId)) return;

    setCancellingRenders((prev) => new Set([...prev, jobId]));

    try {
      await rendersService.cancelRender(jobId);
    } catch (error: unknown) {
      console.error('Cancel job error:', error);
      toast.error(
        error instanceof ApiClientError
          ? error.getFirstError()
          : tErrors('generic')
      );
    } finally {
      setCancellingRenders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{t('recentJobs')}</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col">
          {/* Recent jobs list - compact */}
          {jobsToShow.length > 0 ? (
            <div className="divide-y divide-border">
              {jobsToShow.map((job) => {
                const projectId = job.relationships.project.data?.id;
                const conceptId = job.relationships.concept.data?.id;
                const isAlbum = isAlbumHistoryItem(job);
                const isEstimate = isEstimateHistoryItem(job);
                const isRender = isRenderHistoryItem(job);
                // Estimates open the concept's Estimate tab directly.
                const conceptLink = isEstimate
                  ? `/project/${projectId}/concept/${conceptId}?tab=estimate`
                  : `/project/${projectId}/concept/${conceptId}`;
                const showCancelButton = canCancelJob(job);
                const isCancelling = cancellingRenders.has(job.id);
                const status = job.attributes.status;

                // Get display values
                const conceptName = job.relationships.concept.meta.name;
                const conceptSequenceNumber =
                  job.relationships.concept.meta.project_sequence_number;
                const imageUrl = isRender
                  ? job.attributes.image_url
                  : isEstimate
                    ? job.attributes.image_url
                    : undefined;
                const isMainAngle = isRender
                  ? job.relationships.concept_angle?.meta?.is_main
                  : undefined;
                const isUpscaled = isRender
                  ? job.attributes.is_upscaled
                  : false;

                // Calculate progress for processing jobs
                const progress =
                  status === 'processing'
                    ? calculateJobProgressFromStart(
                        job.attributes.started_at,
                        job.attributes.duration_predicted,
                        job.id
                      )
                    : 0;

                // Build title
                const title = isAlbum
                  ? t('pdfAlbum')
                  : isEstimate
                    ? t('estimate')
                    : conceptName ||
                      tConcept('conceptNumber', {
                        number: conceptSequenceNumber,
                      });

                // Build status text
                let statusText = '';
                if (isAlbum) {
                  // Show concept# and details for album
                  const parts = [
                    tConcept('conceptNumber', {
                      number: conceptSequenceNumber,
                    }),
                  ];
                  if (job.attributes.format)
                    parts.push(job.attributes.format.toUpperCase());
                  if (job.attributes.template)
                    parts.push(job.attributes.template);
                  statusText = parts.join(' • ');
                } else if (isEstimate) {
                  // Concept# + region for estimates (mirrors the album line)
                  const parts = [
                    tConcept('conceptNumber', {
                      number: conceptSequenceNumber,
                    }),
                  ];
                  if (job.attributes.region_label)
                    parts.push(job.attributes.region_label);
                  statusText = parts.join(' • ');
                } else {
                  if (status === 'pending') statusText = t('queued');
                  else if (status === 'processing')
                    statusText = t('processing');
                  else if (status === 'completed') statusText = t('completed');
                  else if (status === 'failed') statusText = t('failed');
                }

                return (
                  <div
                    key={job.id}
                    className="flex items-center gap-3 px-4 py-2.5"
                  >
                    {/* Thumbnail - 44x44 like iOS */}
                    <Link
                      href={conceptLink}
                      className="relative w-11 h-11 rounded-lg overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center"
                      onClick={() => handleOpenChange(false)}
                    >
                      {isAlbum ? (
                        <DraftingCompass className="h-5 w-5 text-muted-foreground" />
                      ) : isEstimate && !imageUrl ? (
                        <Calculator className="h-5 w-5 text-muted-foreground" />
                      ) : imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt=""
                          fill
                          sizes="44px"
                          className="object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted" />
                      )}

                      {/* Status overlay */}
                      {status === 'processing' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <div className="w-full px-1.5">
                            <Progress
                              value={progress}
                              className="h-1 bg-white/30"
                            />
                          </div>
                        </div>
                      )}
                      {status === 'pending' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <PulsingDots size="sm" className="text-white" />
                        </div>
                      )}
                    </Link>

                    {/* Job info */}
                    <Link
                      href={conceptLink}
                      className="flex-1 min-w-0"
                      onClick={() => handleOpenChange(false)}
                    >
                      {/* Title with badges */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-list-item-title text-foreground truncate">
                          {title}
                        </span>
                        {/* Badges */}
                        {isUpscaled && <Badge variant="4k">4K</Badge>}
                        {!isAlbum && isMainAngle === true && (
                          <Badge variant="main">
                            {tConceptCard('badgeMain')}
                          </Badge>
                        )}
                        {!isAlbum && isMainAngle === false && (
                          <Badge variant="secondary">
                            {tConceptCard('badgeSecondary')}
                          </Badge>
                        )}
                      </div>
                      {/* Status */}
                      <div className="text-list-item-meta text-muted-foreground truncate">
                        {statusText}
                      </div>
                    </Link>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Download for completed albums */}
                      {isAlbum &&
                        status === 'completed' &&
                        job.attributes.public_url && (
                          <>
                            <button
                              className="p-1.5 text-muted-foreground hover:text-blue-500 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = job.attributes.public_url!;
                                link.download =
                                  job.attributes.filename || `album.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              title={t('downloadAlbum')}
                            >
                              <Download className="h-5 w-5" />
                            </button>
                            <button
                              className="p-1.5 text-muted-foreground hover:text-blue-500 transition-colors"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await navigator.clipboard.writeText(
                                    job.attributes.public_url!
                                  );
                                  toast.success(t('publicLinkCopied'), {
                                    icon: <Check className="h-4 w-4" />,
                                  });
                                } catch (_error) {
                                  toast.error(t('failedToCopyLink'));
                                }
                              }}
                              title={t('copyPublicLink')}
                            >
                              <Link2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                      {/* Cancel button */}
                      {showCancelButton && (
                        <button
                          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                          onClick={() => handleCancelJob(job.id)}
                          disabled={isCancelling}
                          title={t('cancelRender')}
                        >
                          {isCancelling ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          ) : (
                            <Ban className="h-5 w-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <svg
                className="h-10 w-10 mb-2 opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-list-item-subtitle">{t('noRecentJobs')}</span>
            </div>
          )}

          {/* Queue full message */}
          {isQueueFull && hasQueueLimit && (
            <Link
              href="/account/tokens"
              className="flex items-center justify-between text-list-item-meta text-muted-foreground hover:text-foreground transition-colors px-4 py-2 border-t"
              onClick={() => handleOpenChange(false)}
            >
              <span>{t('queueFullMessage')}</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 ml-2" />
            </Link>
          )}

          {/* Link to full history */}
          <div className="px-4 py-3 border-t">
            <Link href="/history" onClick={() => handleOpenChange(false)}>
              <Button
                variant="secondary"
                className="w-full justify-center h-10"
                size="default"
              >
                {t('fullHistory')}
              </Button>
            </Link>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
